/** What a table is not showing, said next to the table rather than inferred. */

import { TriangleAlert } from "lucide-react";
import { cn } from "~/lib/utils";

/**
 * How much of a collection is on screen, stated beside the thing it counts. The
 * denominator appears only once a filter has narrowed something: a count that
 * always reads "14 of 14" trains a reader to skip it.
 */
export const ShownCount = ({
  shown,
  total,
  noun,
  className,
}: {
  shown: number;
  total: number;
  /** Singular; pluralised with a trailing `s`. */
  noun: string;
  className?: string;
}) => (
  <span className={cn("text-sm tabular-nums text-muted-foreground", className)}>
    {shown < total ? `${shown} of ${total}` : shown}{" "}
    {shown === 1 && shown === total ? noun : `${noun}s`}
  </span>
);

export interface ListTruncation {
  /** What was cut short, as it reads mid-sentence: "cases", "runs". */
  noun: string;
  shown: number;
  total: number;
}

/**
 * That a list stopped short of what the server holds. It invalidates every
 * figure above it rather than qualifying one column, so it sits over both tabs.
 */
export const TruncationNotice = ({ truncations }: { truncations: ListTruncation[] }) => {
  if (truncations.length === 0) {
    return null;
  }

  return (
    <p className="flex items-start gap-1.5 rounded-md border border-orange-300 bg-orange-50 px-3 py-2 text-xs text-orange-800">
      <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
      <span>
        Showing{" "}
        {truncations.map(({ noun, shown, total }) => `${shown} of ${total} ${noun}`).join(" and ")}.
        Every figure here is computed over what loaded, so treat it as a sample of the suite rather
        than the whole of it. Narrow by tag to bring a full slice into range.
      </span>
    </p>
  );
};
